import { API_BASE_URL } from './config';
import { extractErrorMessage, throwApiError } from './errors';
import type { OrgRole } from '@contexts/AuthContextStore';
import type {
  OrgResponse,
  InvitationResponse,
  InvitePreviewResponse,
  CurrentOrgResponse,
} from './types/organizations';

export { ApiError } from './errors';
export type {
  OrgResponse,
  MemberResponse,
  InvitationResponse,
  InvitePreviewResponse,
  CurrentOrgResponse,
} from './types/organizations';

const BASE = `${API_BASE_URL}/api/v1/organizations`;

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Safely parse a JSON response body.
 * Returns the parsed object, or a fallback with the HTTP status text
 * when the body is empty or not valid JSON.
 */
async function safeJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const text = await res.text();
    if (!text) return {};
    return JSON.parse(text);
  } catch {
    return {};
  }
}

// ============================================================================
// Organization CRUD
// ============================================================================

export async function createOrganization(token: string, name: string): Promise<OrgResponse> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ name }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to create organization'));
  return data as unknown as OrgResponse;
}

export async function getCurrentOrganization(token: string): Promise<CurrentOrgResponse> {
  const res = await fetch(`${BASE}/current`, {
    headers: authHeaders(token),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to get organization'));
  return data as unknown as CurrentOrgResponse;
}

export async function updateOrganization(token: string, name: string): Promise<OrgResponse> {
  const res = await fetch(`${BASE}/current`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ name }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to update organization'));
  return data as unknown as OrgResponse;
}

export async function leaveOrganization(token: string): Promise<void> {
  const res = await fetch(`${BASE}/current/leave`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to leave organization'));
  }
}

// ============================================================================
// Members
// ============================================================================

export async function changeRole(token: string, userId: string, role: OrgRole): Promise<void> {
  const res = await fetch(`${BASE}/current/members/${userId}/role`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to change role'));
  }
}

export async function removeMember(token: string, userId: string): Promise<void> {
  const res = await fetch(`${BASE}/current/members/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to remove member'));
  }
}

// ============================================================================
// User Expiry (AIBUILD_ADMIN only)
// ============================================================================

export async function setMemberExpiry(
  token: string,
  userId: string,
  expiryDate: string,
): Promise<void> {
  const res = await fetch(`${BASE}/current/members/${userId}/expiry`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ expiryDate }),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to update expiry date'));
  }
}

// ============================================================================
// Invitations
// ============================================================================

export class MemberLimitError extends Error {
  readonly limit: number;
  constructor(limit: number) {
    super(`Team size limit of ${limit} members reached`);
    this.name = 'MemberLimitError';
    this.limit = limit;
  }
}

export async function sendInvitation(
  token: string,
  email: string,
  role: OrgRole,
): Promise<InvitationResponse> {
  const res = await fetch(`${BASE}/current/invitations`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ email, role }),
  });
  const data = await safeJson(res);
  if (res.status === 422 && (data as { error?: string }).error === 'MEMBER_LIMIT_REACHED') {
    throw new MemberLimitError((data as { limit?: number }).limit ?? 50);
  }
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to send invitation'));
  return data as unknown as InvitationResponse;
}

export async function listInvitations(token: string): Promise<InvitationResponse[]> {
  const res = await fetch(`${BASE}/current/invitations`, {
    headers: authHeaders(token),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to list invitations'));
  return data as unknown as InvitationResponse[];
}

export async function revokeInvitation(token: string, inviteId: string): Promise<void> {
  const res = await fetch(`${BASE}/current/invitations/${inviteId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to revoke invitation'));
  }
}

export async function acceptInvitation(token: string, inviteToken: string): Promise<void> {
  const res = await fetch(`${BASE}/invitations/${inviteToken}/accept`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    if (res.status === 422 && (data as { error?: string }).error === 'MEMBER_LIMIT_REACHED') {
      throw new MemberLimitError((data as { limit?: number }).limit ?? 50);
    }
    throw new Error(extractErrorMessage(data, 'Failed to accept invitation'));
  }
}

export async function declineInvitation(token: string, inviteToken: string): Promise<void> {
  const res = await fetch(`${BASE}/invitations/${inviteToken}/decline`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to decline invitation'));
  }
}

// ============================================================================
// MFA Enforcement
// ============================================================================

export async function updateMfaEnforcement(token: string, enforced: boolean): Promise<OrgResponse> {
  const res = await fetch(`${BASE}/current/mfa-enforcement`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ enforced }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to update MFA enforcement'));
  return data as unknown as OrgResponse;
}

/** Public endpoint — no auth required. */
export async function getInvitePreview(inviteToken: string): Promise<InvitePreviewResponse> {
  const res = await fetch(`${BASE}/invitations/${inviteToken}`);
  const data = await safeJson(res);
  if (!res.ok) throwApiError(res, data, 'Failed to get invitation details');
  return data as unknown as InvitePreviewResponse;
}
